/**
 * buddy-editor-drawer.js
 * Mobile Bottom-Sheet-Drawer fuer den Collection-Filter der NEUEN Architektur
 * (Phase-2, Block 2-1c). Funktional identisch zu buddy-collection-drawer.js, aber:
 *
 *   (a) Init-Logik in window.BuddyEditorDrawer.init() gekapselt (idempotent —
 *       doppelter Aufruf raeumt alte Listener auf bzw. bindet nicht doppelt).
 *   (b) Auto-Init bei DOMContentLoaded bzw. sofort, wenn DOM bereits ready ist.
 *   (c) Re-Init bei shopify:section:load und shopify:section:unload (Theme-Editor),
 *       sodass der Drawer nach Section-Reload neu bindet.
 *
 * Warum eine eigene Datei: assets/buddy-collection-drawer.js ist eingefroren
 * (wird von der LIVE-Architektur genutzt) und exportiert keine Init-Funktion.
 * Diese Datei ist die rebindbare Variante fuer die Phase-2-Sections.
 *
 * Zustaendigkeiten (unveraendert ggue. Original):
 *  - Trigger-Button oeffnet/schliesst Drawer via CSS-Klasse + hidden-Attribut
 *  - Body-Scroll-Lock beim Oeffnen (overflow: hidden auf document.body)
 *  - Focus-Trap: Tab + Shift+Tab bleiben innerhalb des Drawers
 *  - Escape schliesst Drawer und gibt Fokus an Trigger zurueck
 *  - Click auf Overlay (.c-mobile-drawer__overlay) schliesst Drawer
 *  - aria-expanded am Trigger wird synchronisiert
 *  - No-JS-Fallback: Ohne dieses Script bleibt der Filter als sichtbare
 *    Inline-Form stehen (hidden wird erst per JS gesetzt).
 *
 * Media-Query-Guard: Drawer-Logik gilt nur bei <= 768px. Auf Desktop bleibt
 * die Filter-Sidebar unveraendert und der Trigger ist per CSS display:none.
 */

(function () {
  'use strict';

  var DRAWER_ID = 'buddy-mobile-filter-drawer';
  var TRIGGER_SELECTOR = '[data-buddy-drawer-trigger]';
  var OVERLAY_SELECTOR = '.c-mobile-drawer__overlay';
  var CLOSE_SELECTOR = '[data-buddy-drawer-close]';
  var OPEN_CLASS = 'c-mobile-drawer--open';

  // Fokussierbare Elemente fuer den Focus-Trap
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
    ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Open-/Close-Klasse liegt auf dem .c-mobile-drawer Wrapper (nicht auf dem Panel).
  // CSS-Selektoren .c-mobile-drawer--open .c-mobile-drawer__overlay /__panel
  // erwarten den Modifier als Ancestor. panel = Dialog (A11y/Focus-Trap),
  // wrapper = Visual-Toggle.
  var panel = null;   // #buddy-mobile-filter-drawer (role=dialog)
  var wrapper = null; // .c-mobile-drawer (CSS-Open-Ziel)
  var trigger = null;
  var lastFocus = null;
  var isClosing = false; // Guard: verhindert Focus-Trap waehrend der Close-Transition

  // Referenzen auf aktuell gebundene Knoten + Handler — fuer sauberes Aufraeumen
  // bei Re-Init (Idempotenz). Wir merken uns die EXAKTEN Elemente/Funktionen,
  // an die wir Listener gehaengt haben, damit removeEventListener matched.
  var boundTrigger = null;
  var boundOverlay = null;
  var boundCloseBtn = null;
  var keydownBound = false;

  function teardown() {
    if (boundTrigger) {
      boundTrigger.removeEventListener('click', openDrawer);
      boundTrigger = null;
    }
    if (boundOverlay) {
      boundOverlay.removeEventListener('click', closeDrawer);
      boundOverlay = null;
    }
    if (boundCloseBtn) {
      boundCloseBtn.removeEventListener('click', closeDrawer);
      boundCloseBtn = null;
    }
    if (keydownBound) {
      document.removeEventListener('keydown', onKeyDown);
      keydownBound = false;
    }
    // Offen gebliebenen Zustand defensiv aufloesen (z. B. Editor-Reload bei
    // geoeffnetem Drawer).
    if (wrapper) {
      wrapper.classList.remove(OPEN_CLASS);
    }
    document.body.style.overflow = '';
    isClosing = false;
    panel = null;
    wrapper = null;
    trigger = null;
    lastFocus = null;
  }

  function init() {
    // Idempotent: erst alle bisherigen Bindungen loesen, dann frisch binden.
    teardown();

    panel = document.getElementById(DRAWER_ID);
    trigger = document.querySelector(TRIGGER_SELECTOR);

    if (!panel || !trigger) return;

    // Wrapper ist der naechste .c-mobile-drawer Ancestor des Panels
    wrapper = panel.closest('.c-mobile-drawer');
    if (!wrapper) return; // Snippet-Struktur nicht wie erwartet — abbrechen

    // No-JS-Fallback: Wrapper erst jetzt per JS ausblenden.
    wrapper.setAttribute('hidden', '');

    // Aktiven Filter-Count im Trigger-Badge aktualisieren (Badge optional)
    updateTriggerBadge();

    trigger.addEventListener('click', openDrawer);
    boundTrigger = trigger;

    document.addEventListener('keydown', onKeyDown);
    keydownBound = true;

    var overlay = wrapper.querySelector(OVERLAY_SELECTOR);
    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
      boundOverlay = overlay;
    }

    var closeBtn = wrapper.querySelector(CLOSE_SELECTOR);
    if (closeBtn) {
      closeBtn.addEventListener('click', closeDrawer);
      boundCloseBtn = closeBtn;
    }
  }

  function openDrawer() {
    if (!wrapper) return;

    lastFocus = document.activeElement;

    wrapper.removeAttribute('hidden');
    wrapper.classList.add(OPEN_CLASS);
    trigger.setAttribute('aria-expanded', 'true');
    trigger.classList.add('c-mobile-drawer__trigger--active');

    // Body-Scroll-Lock
    document.body.style.overflow = 'hidden';

    // Fokus auf erstes fokussierbares Element im Panel (Dialog-Element)
    var firstFocusable = panel.querySelector(FOCUSABLE);
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  function closeDrawer() {
    if (!wrapper) return;

    isClosing = true;
    wrapper.classList.remove(OPEN_CLASS);
    trigger.setAttribute('aria-expanded', 'false');
    trigger.classList.remove('c-mobile-drawer__trigger--active');

    // Body-Scroll-Lock aufheben
    document.body.style.overflow = '';

    // Fokus sofort zurueck zum Trigger (bevor hidden gesetzt wird)
    if (lastFocus) {
      lastFocus.focus();
      lastFocus = null;
    }

    // Kurze Animation-Delay vor hidden — CSS-Transition kann einhaengen.
    // Waehrend isClosing=true blockiert onKeyDown Focus-Trap + Escape.
    setTimeout(function () {
      if (wrapper) {
        wrapper.setAttribute('hidden', '');
      }
      isClosing = false;
    }, 200);
  }

  function onKeyDown(e) {
    if (!wrapper || wrapper.hasAttribute('hidden') || isClosing) return;

    if (e.key === 'Escape' || e.keyCode === 27) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    if (e.key === 'Tab' || e.keyCode === 9) {
      trapFocus(e);
    }
  }

  function trapFocus(e) {
    var focusable = Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE));
    focusable = focusable.filter(function (el) {
      return !el.closest('[hidden]') && el.offsetParent !== null;
    });

    if (focusable.length === 0) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function updateTriggerBadge() {
    var badge = trigger && trigger.querySelector('.c-mobile-drawer__trigger-count');
    if (!badge) return;

    var count = parseInt(trigger.getAttribute('data-active-count') || '0', 10);
    if (count > 0) {
      badge.textContent = count + ' aktiv';
      badge.removeAttribute('hidden');
    } else {
      badge.setAttribute('hidden', '');
    }
  }

  // Oeffentliche, erneut aufrufbare API
  window.BuddyEditorDrawer = window.BuddyEditorDrawer || {};
  window.BuddyEditorDrawer.init = init;

  // (b) Auto-Init nach DOM-ready bzw. sofort
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // (c) Re-Init im Theme-Editor. Bewusst OHNE Filter auf event.detail.sectionId:
  // init() ist idempotent (teardown vor rebind), und ein erneutes Binden auf
  // die jeweils vorhandenen DOM-Knoten ist immer korrekt. Bei :unload genuegt
  // ein Teardown (Section verschwindet — Scroll-Lock loesen).
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', teardown);
})();
